
import React, { useState } from 'react';
import { Comment } from '@/types/social';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface CommentWithRepliesProps {
  comment: Comment;
  userVotes: Record<string, { up?: boolean; down?: boolean }>;
  upvoteCounts: Record<string, number>;
  downvoteCounts: Record<string, number>;
  maxVotes: number;
  isVoting: Record<string, boolean>;
  handleVote: (commentId: string, voteType: 'up' | 'down') => void;
  onReply?: (commentId: string) => void;
  replyForm: string | null;
  toggleReplyForm: (commentId: string | null) => void;
  replies: Record<string, Comment[]>;
  repliesVisible: Record<string, boolean>;
  repliesLoading: Record<string, boolean>;
  toggleReplies: (commentId: string) => void;
  replyCounts: Record<string, number>;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  handleSubmitComment: (content: string, parentId?: string | null) => void;
}

const CommentWithReplies: React.FC<CommentWithRepliesProps> = ({
  comment,
  userVotes,
  upvoteCounts,
  downvoteCounts,
  maxVotes,
  isVoting,
  handleVote,
  toggleReplyForm,
  replyForm,
  replies,
  repliesVisible,
  repliesLoading,
  toggleReplies,
  replyCounts,
  isAuthenticated,
  isSubmitting,
  handleSubmitComment
}) => {
  const replyCount = replyCounts[comment.id] || 0;
  const hasReplies = replyCount > 0;
  const isShowingReplies = repliesVisible[comment.id] || false;

  return (
    <div className="space-y-3">
      <CommentItem
        comment={comment}
        userVotes={userVotes}
        upvoteCounts={upvoteCounts}
        downvoteCounts={downvoteCounts}
        maxVotes={maxVotes}
        isVoting={isVoting}
        handleVote={handleVote}
        onReply={() => toggleReplyForm(comment.id)}
      />
      
      {/* Reply form */}
      {replyForm === comment.id && (
        <div className="ml-8 mt-2">
          <CommentForm 
            isSubmitting={isSubmitting}
            onSubmit={(content) => handleSubmitComment(content, comment.id)}
            isAuthenticated={isAuthenticated}
            placeholder="Write a reply..."
            buttonText="Reply"
            autoFocus={true}
            onCancel={() => toggleReplyForm(null)}
          />
        </div>
      )}
      
      {/* Show/hide replies button */}
      {hasReplies && (
        <div className="ml-8">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => toggleReplies(comment.id)}
            className="flex items-center text-xs text-muted-foreground"
          >
            {isShowingReplies ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Hide {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* Display replies */}
      {isShowingReplies && (
        <div className="ml-8 mt-2 space-y-4">
          {repliesLoading[comment.id] ? (
            <p className="text-muted-foreground text-center">Loading replies...</p>
          ) : replies[comment.id]?.length > 0 ? (
            replies[comment.id].map((reply) => (
              <CommentItem
                key={`reply-${reply.id}`}
                comment={reply}
                userVotes={userVotes}
                upvoteCounts={upvoteCounts}
                downvoteCounts={downvoteCounts}
                maxVotes={maxVotes}
                isVoting={isVoting}
                handleVote={handleVote}
              />
            ))
          ) : (
            <p className="text-muted-foreground text-center">No replies yet</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentWithReplies;
